import axios from "axios";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { showToast } from "./customToast";
import { downloadProgress } from "./downloadProgressToast";
import { SECURITY_KEY } from "../globals";
import { formatInvoiceNumber, formatDateToDMY } from "./financialYearHelper";


const BASE_API_URL = (process.env.REACT_APP_BASE_API_URL || "http://localhost:5000/api").replace(/\/api\/?$/, "") + "/api";
const BASE_REST_API_URL = process.env.REACT_APP_BASE_REST_API_URL || "";

// Build company logo URL
export const buildLogoUrl = (fileName) => {
  if (!fileName || fileName === "null") return null;
  const path = fileName.toString().trim();
  if (/^https?:\/\//i.test(path)) return path;
  let cleaned = path
    .replace(/^~[\\/]+/, "")
    .replace(/\\/g, "/")
    .replace(/^\/+/, "");
  const restRoot = (BASE_REST_API_URL || "").replace(/\/+$/, "");
  if (/^uploads\//i.test(cleaned)) {
    return `${restRoot.replace(/\/restApi$/i, "")}/${cleaned}`;
  }
  return `${restRoot}/${cleaned}`;
};

export const formatTotalTimeDisplay = (val) => {
  if (
    val === null ||
    val === undefined ||
    val === "" ||
    val === "-" ||
    val === "0" ||
    val === 0 ||
    val === "0h" ||
    val === "0h 0m" ||
    val === "0.00"
  ) {
    return "";
  }
  if (typeof val === "string" && (val.includes("h") || val.includes("m") || val.includes(":"))) {
    return val;
  }
  const num = parseFloat(val);
  if (!isNaN(num) && num > 0) {
    let h = Math.floor(num);
    let m = Math.round((num - h) * 60);
    if (m >= 60) {
      h += 1;
      m = 0;
    }
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }
  return String(val);
};

// Fetch company details from restAPI
export const fetchRestCompany = async (companyId) => {
  if (!companyId || !BASE_REST_API_URL) return {};
  const data = new URLSearchParams();
  data.append("SecurityKey", SECURITY_KEY);
  data.append("TableName", "company");
  data.append("WhereCondition", `CompID=${companyId}`);
  data.append("*", "*");

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 5000);
  try {
    const response = await fetch(`${BASE_REST_API_URL}/SelectData.aspx`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: data.toString(),
      signal: controller.signal,
    });
    const json = await response.json();
    return json.Response?.[0] || {};
  } catch (_) {
    return {};
  } finally {
    clearTimeout(timeout);
  }
};

const format12Hour = (time24) => {
  if (!time24) return "-";
  const [h, m] = time24.split(":");
  let hour = parseInt(h, 10);
  const ap = hour >= 12 ? "PM" : "AM";
  hour = hour % 12;
  if (hour === 0) hour = 12;
  return `${hour}:${m} ${ap}`;
};

const toInputDate = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.split("T")[0].split(" ")[0];
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDisplayDate = (value) => {
  if (!value || value === "null" || value === "undefined" || value === "-") return "";
  const str = String(value).trim();
  if (/^\d{1,2}[-/]\d{1,2}[-/]\d{4}$/.test(str)) {
    const parts = str.split(/[-/]/);
    return `${String(parts[0]).padStart(2, "0")}-${String(parts[1]).padStart(2, "0")}-${parts[2]}`;
  }
  if (/^\d{4}[-/]\d{1,2}[-/]\d{1,2}/.test(str)) {
    const parts = str.split("T")[0].split(" ")[0].split(/[-/]/);
    return `${String(parts[2]).padStart(2, "0")}-${String(parts[1]).padStart(2, "0")}-${parts[0]}`;
  }
  const d = new Date(str);
  if (!isNaN(d.getTime())) {
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  }
  return str;
};

// =========================================================================
// 1. EXACT DUTY SLIP PDF GENERATION (from Dutyslip.jsx with Full Data Fetch)
// =========================================================================
export const downloadDutySlip = async (rowOrId, onProgress) => {
  try {
    downloadProgress.start("Duty Slip PDF", "Connecting to database...");
    if (onProgress) onProgress(15, "Connecting to database...");
    downloadProgress.update(15, "Connecting to database...");
    const selectedCompany = JSON.parse(localStorage.getItem("selectedCompany")) || {};
    const companyId =
      localStorage.getItem("companyId") ||
      selectedCompany.id ||
      selectedCompany.CompID;


    let row = typeof rowOrId === "object" && rowOrId !== null ? { ...rowOrId } : { id: rowOrId };
    if (row._rawRow && typeof row._rawRow === "object") {
      row = { ...row._rawRow, ...row };
    }

    const rawIdCandidate =
      row.id ||
      row.slipId ||
      row.SlipId ||
      row.SlipNo ||
      row.dsNo ||
      row.DutyslipID ||
      row.BookingID ||
      row.dutySlipId ||
      (typeof rowOrId !== "object" ? rowOrId : "");

    let rawId = String(rawIdCandidate || "")
      .replace(/^DS-?/i, "")
      .trim();

    if (rawId === "[object Object]") {
      rawId = "";
    }

    // Fetch full duty slip record from backend
    if (rawId) {
      try {
        const dsRes = await axios.get(`${BASE_API_URL}/dutyslip/dutyslip?id=${rawId}`);
        if (dsRes.data?.success && Array.isArray(dsRes.data?.data) && dsRes.data.data.length > 0) {
          const fetchedRow = dsRes.data.data[0];
          row = {
            ...row,
            ...fetchedRow,
            id: fetchedRow.DutyslipID || rawId,
            dutyslipDate: fetchedRow.DutyslipDate || fetchedRow.slipDate || row.date,
            time12: fetchedRow.time12 || fetchedRow.openTime12 || row.OpnTime || "",
            endTime12: fetchedRow.endTime12 || fetchedRow.closeTime12 || row.CloseTime || "",
            partyName: fetchedRow.PartyName || row.company || row.partyName || "",
            partyAddress: fetchedRow.ClientAddress || fetchedRow.address || fetchedRow.PartyAddress || row.partyAddress || row.address || row.ClientAddress || "",
            address: fetchedRow.ClientAddress || fetchedRow.address || fetchedRow.PartyAddress || row.address || row.partyAddress || row.ClientAddress || "",
            ClientAddress: fetchedRow.ClientAddress || fetchedRow.address || fetchedRow.PartyAddress || row.ClientAddress || "",
            mobile: fetchedRow.Mobile || row.mobile || "",
            partyMobile: fetchedRow.Mobile || row.mobile || "",
            hiredMobile: fetchedRow.Mobile || row.mobile || "",
            vehicleType: fetchedRow.VehicleType || row.vehicleType || row.vehicle || "",
            vehicleNo: fetchedRow.VehicleNo || row.vehicleNo || "",
            guestName: fetchedRow.ClientName || row.guestName || row.bookedBy || "",
            driverName: fetchedRow.DriverName || row.driverName || "",
            fareId: fetchedRow.FareId || fetchedRow.fareId || null,
            packageName: fetchedRow.PackageName || fetchedRow.packageName || row.packageName || "",
            kmStart: fetchedRow.KMStart ?? row.kmStart ?? 0,
            kmEnd: fetchedRow.KMClose ?? row.kmEnd ?? 0,
            totalKm: fetchedRow.TotalKM ?? row.totalKm ?? 0,
            totalTime: fetchedRow.TotalTime || row.totalTime || 0,
            dateWiseDetails: fetchedRow.dateWiseDetails || row.dateWiseDetails || [],
          };
        }
      } catch (err) {
        console.warn("Using summary duty slip data for PDF:", err.message);
      }
    }

    if (onProgress) onProgress(45, "Loading company info and logo...");
    downloadProgress.update(45, "Loading company info and logo...");
    let companyData = {};
    let companyLogoUrl = null;

    if (companyId) {
      try {
        const companyRes = await axios.get(
          `${BASE_API_URL}/invoice/company/${companyId}`,
          { timeout: 5000 },
        );
        if (companyRes.data?.success) {
          companyData = companyRes.data.data || {};
        }
      } catch (err) {
        console.warn("Failed to fetch company info for duty slip PDF", err);
      }

      try {
        const restCompanyData = await fetchRestCompany(companyId);
        companyData = {
          ...restCompanyData,
          ...companyData,
          LogoPath:
            companyData.LogoPath ||
            companyData.Logopath ||
            companyData.logoPath ||
            restCompanyData.LogoPath,
        };
      } catch (err) {
        console.warn("Failed to fetch company logo for duty slip PDF", err);
      }
    }

    const companyName =
      companyData.Name ||
      companyData.name ||
      selectedCompany.Name ||
      selectedCompany.name ||
      "DAZY TOURE TRAVEL'S";
    const companyAddress =
      companyData.Address ||
      companyData.address ||
      selectedCompany.Address ||
      selectedCompany.address ||
      "Sahakar Marg";
    companyLogoUrl = buildLogoUrl(
      companyData.LogoPath ||
        companyData.Logopath ||
        companyData.logoPath ||
        selectedCompany?.LogoPath ||
        selectedCompany?.Logopath ||
        selectedCompany?.logoPath
    );

    const companyColor =
      companyData.CompanyColourCode ||
      companyData.companyColourCode ||
      selectedCompany?.CompanyColourCode ||
      "#000000";

    const getPdfLogoUrl = (imageUrl) => {
      if (!imageUrl) return null;
      try {
        const parsed = new URL(imageUrl);
        const proxyApiUrl = BASE_API_URL || "http://localhost:5000/api";
        return `${proxyApiUrl}/invoice/logo-proxy?path=${encodeURIComponent(
          parsed.pathname,
        )}`;
      } catch (err) {
        return imageUrl;
      }
    };

    const loadImageAsDataUrl = async (imageUrl) => {
      if (!imageUrl) return null;
      try {
        const response = await axios.get(imageUrl, {
          responseType: "blob",
          timeout: 5000,
        });
        const blob = response.data;
        return await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        console.warn(
          "Unable to load logo image as blob for PDF:",
          imageUrl,
          error,
        );
        return null;
      }
    };

    const ensureRemoteImageLoads = async (imageUrl) => {
      if (!imageUrl) return null;
      return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(imageUrl);
        img.onerror = () => resolve(null);
        img.src = imageUrl;
        setTimeout(() => resolve(null), 10000);
      });
    };

    const pdfLogoUrl = getPdfLogoUrl(companyLogoUrl);
    const safeLogoUrl = pdfLogoUrl || companyLogoUrl;

    let inlineCompanyLogo = await loadImageAsDataUrl(safeLogoUrl);
    if (!inlineCompanyLogo && safeLogoUrl) {
      inlineCompanyLogo = await ensureRemoteImageLoads(safeLogoUrl);
    }
    const logoSrc = inlineCompanyLogo || companyLogoUrl;

    const waitForImageLoad = async (parent) => {
      const img = parent.querySelector("img.company-logo");
      if (!img) return;
      if (img.complete && img.naturalWidth !== 0) return;
      await new Promise((resolve) => {
        const timeout = setTimeout(resolve, 10000);
        img.addEventListener("load", () => {
          clearTimeout(timeout);
          resolve();
        });
        img.addEventListener("error", () => {
          clearTimeout(timeout);
          resolve();
        });
      });
    };

    const slipId = String(
      row.id ||
      row.slipId ||
      row.SlipId ||
      row.DutyslipID ||
      row.SlipNo ||
      row.dsNo ||
      row.dutySlipId ||
      (rawId && rawId !== "[object Object]" ? rawId : "") ||
      ""
    ).replace(/^DS-?/i, "").replace(/\[object\s+Object\]/gi, "").trim();
    const slipDate = row.dutyslipDate || row.slipDate || row.StartDate || row.date || "";
    const journeyStartDate =
      row.openDate ||
      row.openDutyslipDate ||
      row.OpeningDate ||
      (Array.isArray(row.dateWiseDetails) && row.dateWiseDetails.length > 0
        ? row.dateWiseDetails[0].date || row.dateWiseDetails[0].TripDate
        : null) ||
      slipDate;
    const startTime = row.time12 || (row.time ? format12Hour(row.time) : (row.OpnTime ? format12Hour(row.OpnTime) : "-"));
    const endTime = row.endTime12 || (row.endTime ? format12Hour(row.endTime) : (row.CloseTime ? format12Hour(row.CloseTime) : "-"));
    const kmStart = row.kmStart != null && row.kmStart !== "" ? row.kmStart : (row.KMStart != null ? row.KMStart : "");
    const kmEnd = row.kmEnd != null && row.kmEnd !== "" ? row.kmEnd : (row.KMClose != null ? row.KMClose : "");
    const partyName = row.partyName || row.company || row.PartyName || "";
    const partyAddress = row.partyAddress || row.address || row.ClientAddress || row.PartyAddress || "";
    const guestName = row.guestName || row.bookedBy || row.ClientName || "";
    const hiredMobile = row.partyMobile || row.hiredMobile || row.partyPhone || row.mobile || row.Mobile || "";
    const packageName = row.packageName || row.PackageName || "-";
    const vehicleType = row.vehicleType || row.vehicle || row.CarType || "";
    const vehicleNo = row.vehicleNo || row.VehicleNo || "";
    const taxiDisplay = vehicleType && vehicleNo
      ? `${vehicleType} - ${vehicleNo}`
      : (vehicleType || vehicleNo || "");
    const driverName = row.driverName || row.DriverName || "";
    const totalKm = row.totalKm != null && row.totalKm !== "" ? row.totalKm : (row.TotalKM != null ? row.TotalKM : "");

    let rawTimeVal = row.totalTime != null && row.totalTime !== "" ? row.totalTime : row.TotalTime;
    let totalTime = formatTotalTimeDisplay(rawTimeVal) || "-";

    const tripRows = Array.isArray(row.dateWiseDetails) && row.dateWiseDetails.length > 0
      ? row.dateWiseDetails
      : [
          {
            date: journeyStartDate,
            kmStartGarage: kmStart,
            kmStartUser: kmStart,
            kmClose: kmEnd,
            kmCloseGarage: kmEnd,
            totalKm: totalKm,
            openingTime: startTime === "-" ? "" : startTime,
            closingTime: endTime === "-" ? "" : endTime,
            totalTime: totalTime === "-" ? "" : totalTime,
            tripDetails: row.tripDetails || row.TripDetails || row.route || "",
            packageName: packageName,
          },
        ];

    const escapeHtml = (value) =>
      String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");

    const formatKmPdfCell = (val) => {
      if (val === null || val === undefined || val === "" || val === "0" || val === 0 || Number(val) === 0) {
        return "";
      }
      return String(val);
    };

    const formatTimePdfCell = (val) => {
      if (
        val === null ||
        val === undefined ||
        val === "" ||
        val === "-" ||
        val === "12:00 AM" ||
        val === "12:00 am" ||
        val === "12:00PM" ||
        val === "00:00" ||
        val === "0h 0m" ||
        val === "0h" ||
        val === "0.00" ||
        val === "0" ||
        val === 0
      ) {
        return "";
      }
      return formatTotalTimeDisplay(val);
    };

    const tripRowsHtml = tripRows
      .map(
        (detail) => `
        <tr>
          <td style="border:1px solid #000; padding:6px 4px; text-align:center; height:26px;">${escapeHtml(formatDisplayDate(detail.date))}</td>
          <td style="border:1px solid #000; padding:6px 4px; text-align:center; height:26px;">${escapeHtml(formatKmPdfCell(detail.kmStartUser ?? detail.kmStart ?? detail.kmStartGarage))}</td>
          <td style="border:1px solid #000; padding:6px 4px; text-align:center; height:26px;">${escapeHtml(formatKmPdfCell(detail.kmClose ?? detail.kmEnd ?? detail.kmCloseGarage))}</td>
          <td style="border:1px solid #000; padding:6px 4px; text-align:center; height:26px;">${escapeHtml(formatKmPdfCell(detail.totalKm))}</td>
          <td style="border:1px solid #000; padding:6px 4px; text-align:center; height:26px;">${escapeHtml(formatTimePdfCell(detail.openingTime))}</td>
          <td style="border:1px solid #000; padding:6px 4px; text-align:center; height:26px;">${escapeHtml(formatTimePdfCell(detail.closingTime))}</td>
          <td style="border:1px solid #000; padding:6px 4px; text-align:center; height:26px;">${escapeHtml(formatTimePdfCell(detail.totalTime))}</td>
          <td style="border:1px solid #000; padding:6px 6px; text-align:left; height:26px;">${escapeHtml(detail.tripDetails || "")}</td>
          <td style="border:1px solid #000; padding:6px 6px; text-align:left; height:26px;">${escapeHtml(detail.packageName || detail.PackageName || packageName || "")}</td>
        </tr>
      `,
      )
      .join("");

    const minRows = 5;
    const blankCount = Math.max(0, minRows - tripRows.length);
    const blankRowsHtml = `
      <tr>
        <td style="border:1px solid #000; height:26px;">&nbsp;</td>
        <td style="border:1px solid #000; height:26px;">&nbsp;</td>
        <td style="border:1px solid #000; height:26px;">&nbsp;</td>
        <td style="border:1px solid #000; height:26px;">&nbsp;</td>
        <td style="border:1px solid #000; height:26px;">&nbsp;</td>
        <td style="border:1px solid #000; height:26px;">&nbsp;</td>
        <td style="border:1px solid #000; height:26px;">&nbsp;</td>
        <td style="border:1px solid #000; height:26px;">&nbsp;</td>
        <td style="border:1px solid #000; height:26px;">&nbsp;</td>
      </tr>
    `.repeat(blankCount);

    const logoInitials = (companyName || "")
      .split(" ")
      .map((w) => w[0])
      .join("")
      .slice(0, 3)
      .toUpperCase();

    if (onProgress) onProgress(75, "Rendering high-res document canvas...");
    downloadProgress.update(75, "Rendering high-res document canvas...");
    const element = document.createElement("div");
    element.style.position = "fixed";
    element.style.top = "-9999px";
    element.style.left = "-9999px";
    element.style.width = "1060px";
    element.style.padding = "24px 32px 36px";
    element.style.background = "#ffffff";
    element.style.fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';
    element.style.color = "#0f172a";
    element.style.fontSize = "13px";
    element.style.boxSizing = "border-box";
    element.innerHTML = `
      <style>
        * { box-sizing: border-box; }
        .duty-pdf-table {
          width: 100%;
          border-collapse: collapse;
          table-layout: fixed;
          font-size: 12px;
        }
        .duty-pdf-table th,
        .duty-pdf-table td {
          border: 1px solid #000;
          padding: 6px 4px;
          text-align: center;
          vertical-align: middle;
          word-break: break-word;
        }
        .duty-pdf-table th {
          font-size: 12px;
          font-weight: 700;
          color: #0f172a;
          background-color: #ffffff;
          line-height: 1.25;
        }
        .duty-pdf-table td {
          font-size: 12px;
          color: #000000;
          height: 26px;
        }
        .header-block {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding-bottom: 12px;
          border-bottom: 2px solid #000;
          margin-bottom: 16px;
        }
        .company-info {
          display: flex;
          align-items: center;
          gap: 14px;
        }
        .company-logo-box {
          width: 52px;
          height: 52px;
          background: #000000;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          flex-shrink: 0;
        }
        .company-name {
          font-size: 22px;
          font-weight: 900;
          letter-spacing: 0.5px;
          text-transform: uppercase;
          color: ${companyColor || "#000000"};
          line-height: 1.2;
        }
        .company-address {
          font-size: 13px;
          color: #64748b;
          font-weight: 500;
          margin-top: 2px;
        }
        .duty-title {
          font-size: 24px;
          font-weight: 900;
          text-decoration: underline;
          text-underline-offset: 4px;
          color: #000000;
          letter-spacing: 1.5px;
        }
        .details-grid {
          display: grid;
          grid-template-columns: 1.5fr 1fr;
          gap: 20px;
          border: 1.5px solid #000;
          border-radius: 12px;
          padding: 14px 18px;
          margin-bottom: 16px;
          background: #ffffff;
          font-size: 13px;
          line-height: 1.8;
        }
        .details-item {
          display: flex;
          flex-direction: column;
          gap: 3px;
        }
        .details-row {
          display: grid;
          grid-template-columns: 80px 1fr;
          align-items: baseline;
        }
        .details-row span {
          color: #64748b;
          font-weight: 500;
        }
        .details-row b {
          color: #000000;
          font-weight: 800;
          word-break: break-word;
        }
        .table-container {
          border: 1.5px solid #000;
          border-radius: 8px;
          overflow: hidden;
          margin-bottom: 24px;
          background: #ffffff;
        }
        .signature-area {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-top: 24px;
          padding: 0 4px;
        }
        .signature-block {
          width: 44%;
        }
        .signature-label, .remark-label {
          font-weight: 700;
          font-size: 14px;
          color: #1e293b;
          text-decoration: underline;
          text-underline-offset: 3px;
          margin-bottom: 6px;
          text-align: left;
        }
        .signature-line, .remark-line {
          border-top: 2px solid #000;
          width: 100%;
          margin-top: 6px;
        }
        .remark-block {
          width: 44%;
        }
        .remark-second-line {
          margin-top: 28px;
        }
        .grand-total-row td {
          font-weight: 700;
          font-size: 12.5px;
          color: #000000;
          background-color: #f1f5f9 !important;
          border-top: 2px solid #000 !important;
        }
      </style>

      <div class="header-block">
        <div class="company-info">
          <div class="company-logo-box">
            ${
              logoSrc
                ? `<img class="company-logo" crossorigin="anonymous" src="${logoSrc}" alt="logo" style="width:100%; height:100%; object-fit:contain;" />`
                : `<span style="color:#ffffff; font-weight:800; font-size:16px; letter-spacing:1px;">${escapeHtml(logoInitials || "TRY")}</span>`
            }
          </div>
          <div>
            <div class="company-name">${escapeHtml(companyName)}</div>
            <div class="company-address">${escapeHtml(companyAddress)}</div>
          </div>
        </div>
        <div class="duty-title">DUTY SLIP</div>
      </div>

      <div class="details-grid">
        <div class="details-item">
          <div class="details-row"><span>Sr No:</span><b>${escapeHtml(slipId)}</b></div>
          <div class="details-row"><span>Taxi No:</span><b>${escapeHtml(taxiDisplay)}</b></div>
          <div class="details-row"><span>Hired:</span><b>${escapeHtml(partyName)}</b></div>
          <div class="details-row"><span>Client:</span><b>${escapeHtml(guestName)}</b></div>
          <div class="details-row"><span>Address:</span><b>${escapeHtml(partyAddress)}</b></div>
        </div>
        <div class="details-item">
          <div class="details-row" style="grid-template-columns:85px 1fr;"><span>Date:</span><b>${escapeHtml(formatDisplayDate(journeyStartDate))}</b></div>
          <div class="details-row" style="grid-template-columns:85px 1fr;"><span>Driver:</span><b>${escapeHtml(driverName)}</b></div>
          <div class="details-row" style="grid-template-columns:85px 1fr;"><span>Mobile No:</span><b>${escapeHtml(hiredMobile)}</b></div>
        </div>
      </div>

      <div class="table-container">
        <table class="duty-pdf-table">
          <thead>
            <tr>
              <th style="width:10%;">Date</th>
              <th style="width:10%;">KMStart</th>
              <th style="width:10%;">KMClose</th>
              <th style="width:10%;">TotalKM</th>
              <th style="width:9%;">Opening<br/>Time</th>
              <th style="width:9%;">Closing<br/>Time</th>
              <th style="width:10%;">Total<br/>Time</th>
              <th style="width:18%;">Trip Details</th>
              <th style="width:14%;">Package</th>
            </tr>
          </thead>
          <tbody>
            ${tripRowsHtml}
            ${blankRowsHtml}
            <tr class="grand-total-row">
              <td colspan="3" style="text-align:right; padding-right:16px;">Grand Total</td>
              <td>${escapeHtml(formatKmPdfCell(totalKm))}</td>
              <td colspan="2"></td>
              <td>${escapeHtml(formatTimePdfCell(totalTime))}</td>
              <td colspan="2"></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="signature-area">
        <div class="signature-block">
          <div class="signature-label">Signature</div>
          <div class="signature-line"></div>
        </div>
        <div class="remark-block">
          <div class="remark-label">Remark</div>
          <div class="remark-line"></div>
          <div class="remark-line remark-second-line"></div>
        </div>
      </div>
    `;

    document.body.appendChild(element);
    await waitForImageLoad(element);
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      allowTaint: false,
      logging: false,
      backgroundColor: "#ffffff",
    });
    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const margin = 8;
    const availableWidth = pdfWidth - 2 * margin;
    const availableHeight = pdfHeight - 2 * margin;

    let imgWidth = availableWidth;
    let imgHeight = (canvas.height * availableWidth) / canvas.width;

    if (imgHeight > availableHeight) {
      imgHeight = availableHeight;
      imgWidth = (canvas.width * availableHeight) / canvas.height;
    }

    const xOffset = (pdfWidth - imgWidth) / 2;
    const yOffset = (pdfHeight - imgHeight) / 2;

    pdf.addImage(imgData, "PNG", xOffset, yOffset, imgWidth, imgHeight);
    if (onProgress) onProgress(95, "Compiling A4 PDF...");
    downloadProgress.update(95, "Compiling A4 PDF...");
    pdf.save(`DutySlip_${slipId || slipDate || "document"}.pdf`);
    document.body.removeChild(element);
    if (onProgress) {
      onProgress(100, "Duty Slip downloaded successfully!");
    }
    downloadProgress.complete("Duty Slip downloaded successfully!", "Downloaded Duty Slip PDF");
  } catch (err) {
    console.error("Duty Slip PDF ERROR:", err);
    downloadProgress.error("Failed to download duty slip PDF!");
    showToast.error("Failed to download duty slip PDF!");
  }
};

// =========================================================================
// 2. EXACT INVOICE HTML & PDF GENERATION (from ViewInvoice.jsx)
// =========================================================================
export const buildInvoiceHtml = ({ fullInvoice = {}, companyData = {}, bankData = {}, logoSrc = null }) => {
  const companyName = companyData.Name || companyData.name || "DAZY TOURE TRAVEL'S";
  const companyColor = companyData.CompanyColourCode || companyData.companyColourCode || "#000000";
  const companyAddress = companyData.Address || companyData.address || "Sahakar Marg";
  const companyPhone = companyData.TelNo || companyData.Tel || companyData.Phone || companyData.phone || companyData.Mobile || companyData.mobile || "";
  const companyGSTIN = (
    companyData.GSTNO ||
    companyData.GSTIN ||
    companyData.GstNo ||
    companyData.gstNo ||
    companyData.gstin ||
    companyData.Gstin ||
    companyData.GST_NO ||
    ""
  );

  const invoiceNo = fullInvoice.InvoiceNo || fullInvoice.invId || fullInvoice.invoiceNo || "";
  const invoiceDate = fullInvoice.InvoiceDate || (fullInvoice.items && fullInvoice.items[0] && (fullInvoice.items[0].OpeningDate || fullInvoice.items[0].Date));
  
  const allDutySlipNos = [];
  (fullInvoice.items || []).forEach((item) => {
    const p = item.Particulars || item.particulars || "";
    const parts = p.includes(" || ") ? p.split(" || ") : [p];
    parts.forEach((part) => {
      try {
        if (part.startsWith("{")) {
          const parsed = JSON.parse(part);
          if (parsed.slip) allDutySlipNos.push(String(parsed.slip));
        }
      } catch (_) { }
    });
  });
  if (fullInvoice.DutySlipNo && !allDutySlipNos.includes(String(fullInvoice.DutySlipNo))) {
    allDutySlipNos.unshift(String(fullInvoice.DutySlipNo));
  }
  const uniqueDutySlips = [...new Set(allDutySlipNos)];
  const dutySlipNoDisplay = uniqueDutySlips.length > 0
    ? uniqueDutySlips.map((s) => `${s}`).join(", ")
    : (fullInvoice.DutySlipNo ? `${fullInvoice.DutySlipNo}` : "");

  const taxiNo = fullInvoice.VehicleNo || fullInvoice.vehicleNo || "";
  const vehicleType = fullInvoice.VehicleType || fullInvoice.vehicleType || "";
  const allVehicles = [];
  (fullInvoice.items || []).forEach((item) => {
    const p = item.Particulars || item.particulars || "";
    const parts = p.includes(" || ") ? p.split(" || ") : [p];
    parts.forEach((part) => {
      try {
        if (part.startsWith("{")) {
          const parsed = JSON.parse(part);
          const vNo = (parsed.vehicleNo || parsed.VehicleNo || "").trim();
          const vType = (parsed.vehicleType || parsed.VehicleType || "").trim();
          if (vNo || vType) {
            const label = vNo && vType ? `${vNo} (${vType})` : (vNo || vType);
            if (label && !allVehicles.includes(label)) {
              allVehicles.push(label);
            }
          }
        }
      } catch (_) { }
    });
  });
  if (allVehicles.length === 0 && (taxiNo || vehicleType)) {
    const fallbackLabel = taxiNo && vehicleType ? `${taxiNo} (${vehicleType})` : (taxiNo || vehicleType);
    if (fallbackLabel) allVehicles.push(fallbackLabel);
  }
  const vehicleDisplay = allVehicles.join(", ");
  const driverName = fullInvoice.DriverName || fullInvoice.driverName || "";
  const isReverseCharge = !!(
    fullInvoice.ReverseCharges === true ||
    fullInvoice.ReverseCharges === 1 ||
    fullInvoice.ReverseCharges === "Yes" ||
    fullInvoice.ReverseCharges === "1" ||
    fullInvoice.ReverseCharges === "true" ||
    fullInvoice.ReverseCharge === true ||
    fullInvoice.ReverseCharge === 1 ||
    fullInvoice.ReverseCharge === "Yes" ||
    fullInvoice.ReverseCharge === "1" ||
    fullInvoice.ReverseCharge === "true"
  );
  const reverseCharge = isReverseCharge ? "Yes" : "No";
  const partyName = fullInvoice.PartyName || fullInvoice.company || fullInvoice.partyName || "";
  const guest = fullInvoice.Guest || fullInvoice.ClientName || fullInvoice.guestName || fullInvoice.guest || "";
  const bookedBy = fullInvoice.BookedBy || fullInvoice.bookedBy || "";
  const address = fullInvoice.Address || fullInvoice.PartyAddress || fullInvoice.clientAddress || fullInvoice.address || "";
  let rawState = String(fullInvoice.State || fullInvoice.state || fullInvoice.StateName || fullInvoice.stateName || "").trim();
  let stateCode = String(fullInvoice.StateCode || fullInvoice.stateCode || "").trim();

  // If state contains code in parenthesis e.g. "Andaman & Nicobar Islands (34)"
  const codeInParenMatch = rawState.match(/\((\d{1,2})\)/);
  if (codeInParenMatch) {
    if (!stateCode) stateCode = codeInParenMatch[1].padStart(2, "0");
    rawState = rawState.replace(/\s*\(\d{1,2}\)\s*$/, "").trim();
  }
  const state = rawState;
  const gstin = fullInvoice.GSTIN || fullInvoice.PartyGSTIN || fullInvoice.gstin || fullInvoice.GstNo || fullInvoice.gstNo || "";

  const totalAmount = fullInvoice.totalAmount || 0;
  const taxTotal = fullInvoice.totalTax || 0;
  const lessAdvance = Number(fullInvoice.Discount || fullInvoice.Advance || 0);

  const companyStateCode = String(companyData.StateCode || companyData.State || "08").trim();
  const partyStateCode = String(stateCode || (gstin ? gstin.substring(0, 2) : "") || companyStateCode).trim();
  const isInterState = partyStateCode && companyStateCode && (partyStateCode !== companyStateCode);

  const numberToWords = (num) => {
    const a = [
      "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
      "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
      "Seventeen", "Eighteen", "Nineteen",
    ];
    const b = [
      "", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety",
    ];
    const convert = (n) => {
      if (n < 20) return a[n];
      if (n < 100)
        return b[Math.floor(n / 10)] + (n % 10 ? " " + a[n % 10] : "");
      if (n < 1000)
        return (
          a[Math.floor(n / 100)] +
          " Hundred" +
          (n % 100 ? " " + convert(n % 100) : "")
        );
      if (n < 100000)
        return (
          convert(Math.floor(n / 1000)) +
          " Thousand" +
          (n % 1000 ? " " + convert(n % 1000) : "")
        );
      if (n < 10000000)
        return (
          convert(Math.floor(n / 100000)) +
          " Lakh" +
          (n % 100000 ? " " + convert(n % 100000) : "")
        );
      return (
        convert(Math.floor(n / 10000000)) +
        " Crore" +
        (n % 10000000 ? " " + convert(n % 10000000) : "")
      );
    };
    if (!num) return "Zero Rupees Only";
    return convert(Math.round(num)) + " Rupees Only";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const months = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
    ];
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return String(dateStr);
    const day = ("0" + date.getDate()).slice(-2);
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const formatDateRange = (openDate, closeDate) => {
    const open = formatDate(openDate);
    const close = formatDate(closeDate);
    if (!open) return "";
    if (!close || open === close) return open;
    return `${open} to ${close}`;
  };

  const isDutySlipService = (svcName) => {
    if (!svcName) return false;
    const s = svcName.trim().toLowerCase();
    return (
      s === "transport charges" ||
      s === "transport with duty slip" ||
      s === "transport without duty slip" ||
      s === "taxi fare" ||
      s.includes("transport") ||
      s.includes("duty slip")
    );
  };

  const formatNumber = (num, maxDigits = 20) => {
    if (!num) return "0.00";
    const str = Number(num).toFixed(2).toString();
    return str.length > maxDigits ? str.slice(0, maxDigits) : str;
  };

  const parseOldParticulars = (str, svcLabel) => {
    if (!str) return null;
    const hasDutySlip = /Duty Slip\s*#\d+/i.test(str);
    if (!hasDutySlip) return null;

    let pkg = "", slip = "", dateRange = "", route = "";
    let totalKM = 0, totalTime = 0, baseFare = 0, incKM = 0;
    let extraKM = 0, kmRate = 0, kmCharge = 0;
    let extraMins = 0, timeRate = 0, timeCharge = 0, amount = 0;

    const slipMatch = str.match(/^(.+?)\s*[-—]\s*Duty Slip\s*#(\d+)/i);
    if (slipMatch) { pkg = slipMatch[1].trim(); slip = slipMatch[2]; }

    const hasPipes = str.includes(" | ");
    if (hasPipes) {
      const parts = str.split(" | ");
      const datePart = parts.find(p => /^Date\s*:/i.test(p));
      const routePart = parts.find(p => /^Route\s*:/i.test(p));
      const pkgPart = parts.find(p => /^Package\s*:/i.test(p));
      if (datePart) dateRange = datePart.replace(/^Date\s*:\s*/i, "").trim();
      if (routePart) route = routePart.replace(/^Route\s*:\s*/i, "").trim();
      if (pkgPart && !pkg) pkg = pkgPart.replace(/^Package\s*:\s*/i, "").trim();
    }

    const kmMatch = str.match(/(\d+(?:\.\d+)?)\s*km[,\s]+(\d+(?:\.\d+)?)\s*hrs/i);
    if (kmMatch) { totalKM = Number(kmMatch[1]); totalTime = Number(kmMatch[2]); }

    const baseMatch = str.match(/Base\s*[₹Rs.]+\s*([\d,]+(?:\.\d+)?)/i);
    if (baseMatch) baseFare = Number(baseMatch[1].replace(/,/g, ""));

    const extraKmMatch = str.match(/(\d+(?:\.\d+)?)\s*km\s*extra\s*[×x×]\s*[₹Rs.]+\s*([\d,]+(?:\.\d+)?)\/km/i);
    if (extraKmMatch) {
      extraKM = Number(extraKmMatch[1]);
      kmRate = Number(extraKmMatch[2].replace(/,/g, ""));
      kmCharge = Math.round(extraKM * kmRate * 100) / 100;
    }

    const extraTimeMatch = str.match(/(\d+(?:\.\d+)?)\s*hrs?\s*extra\s*[×x×]\s*[₹Rs.]+\s*([\d,]+(?:\.\d+)?)\/\(?:hr|h|min|m\)?/i);
    if (extraTimeMatch) {
      const extraHrsVal = Number(extraTimeMatch[1]);
      timeRate = Number(extraTimeMatch[2].replace(/,/g, ""));
      extraMins = Math.round(extraHrsVal * 60);
      timeCharge = Math.round(extraHrsVal * timeRate * 100) / 100;
    }

    const allAmounts = [...str.matchAll(/[₹Rs.]+\s*([\d,]+(?:\.\d+)?)/g)];
    if (allAmounts.length) {
      const last = allAmounts[allAmounts.length - 1];
      amount = Number(last[1].replace(/,/g, ""));
    }

    incKM = Math.max(0, totalKM - extraKM);
    if (!pkg && svcLabel) pkg = svcLabel;

    return {
      pkg: pkg.toUpperCase(), slip, dateRange, route, totalKM, totalTime,
      baseFare, incKM, extraKM, kmRate, kmCharge,
      extraMins, timeRate, timeCharge, amount
    };
  };

  const allItems = fullInvoice.items || [];
  const seenDutySlipsInPdf = new Set();
  allItems.forEach((item) => {
    const p = item.Particulars || item.particulars || "";
    const parts = p.includes(" || ") ? p.split(" || ") : [p];
    parts.forEach((part) => {
      try {
        if (part.startsWith("{")) {
          const parsed = JSON.parse(part);
          if (parsed.slip) seenDutySlipsInPdf.add(String(parsed.slip));
        }
      } catch (_) { }
    });
  });

  const addRow = (hsn, dateStr, particulars, rate, amount, opts = {}) => {
    const indent = opts.indent ? "padding-left:14px;" : "";
    const paddingY = opts.compact ? "padding:4px 6px;" : "padding:6px 6px;";
    const rowCellStyle = `${paddingY} border-left:1px solid #000; border-right:1px solid #000; border-bottom:none; font-size:11px; vertical-align:top;`;

    let rateDisplay = "";
    if (rate !== undefined && rate !== null && rate !== "") {
      rateDisplay = (typeof rate === "number" || (!isNaN(rate) && !String(rate).includes("<")))
        ? Number(rate).toFixed(2)
        : String(rate);
    }

    let amountDisplay = "";
    if (amount !== undefined && amount !== null && amount !== "") {
      amountDisplay = (typeof amount === "number" || (!isNaN(amount) && !String(amount).includes("<")))
        ? Number(amount).toFixed(2)
        : String(amount);
    }

    return `
<tr>
  <td style="${rowCellStyle} text-align:center; width:75px;">${hsn || ""}</td>
  <td style="${rowCellStyle} text-align:center; width:105px;">${dateStr || ""}</td>
  <td style="${rowCellStyle} width:380px; ${indent}">${particulars || ""}</td>
  <td style="${rowCellStyle} text-align:right; width:90px;">${rateDisplay}</td>
  <td style="${rowCellStyle} text-align:right; width:100px;">${amountDisplay}</td>
</tr>`;
  };

  let sumOfItemAmounts = 0;
  let itemRows = "";
  let totalRowCount = 0;
  allItems.forEach((item) => {
    const rawParticulars = item.Particulars || item.particulars || "";
    const rawService = item.Service || item.service || "";
    const svcLower = rawService.trim().toLowerCase();

    const slipParts = (rawParticulars.includes(" || ") && rawParticulars.startsWith("{"))
      ? rawParticulars.split(" || ")
      : [rawParticulars];

    const seenSlipsInRow = new Set();
    const uniqueSlipParts = slipParts.filter((sp) => {
      if (!sp.startsWith("{")) return true;
      try {
        const p = JSON.parse(sp);
        if (p && p.slip) {
          const sId = String(p.slip);
          if (seenSlipsInRow.has(sId)) return false;
          seenSlipsInRow.add(sId);
        }
      } catch (_) {}
      return true;
    });

    uniqueSlipParts.forEach((singlePart, partIdx) => {
      if ((svcLower === "transport charges" || svcLower === "transport with duty slip" || svcLower === "taxi fare" || svcLower.includes("transport")) && !singlePart.startsWith("{")) {
        const slipMatch = singlePart.match(/#(\d+)/);
        if (slipMatch && seenDutySlipsInPdf.has(String(slipMatch[1]))) {
          return;
        }
      }

      let parsedData = null;
      try {
        if (singlePart.startsWith("{")) parsedData = JSON.parse(singlePart);
      } catch (_) { }

      if (!parsedData) {
        parsedData = parseOldParticulars(singlePart, rawService);
      }

      const isDutySlipSvc = isDutySlipService(rawService) || !!(parsedData && (parsedData.slip || (parsedData.days && parsedData.days.length > 0)));
      const openDate = item.OpeningDate || item.Date || item.date;
      const closeDate = item.ClosingDate || item.Date || item.date;
      const dateDisplay = (parsedData && parsedData.dateRange)
        ? parsedData.dateRange
        : (isDutySlipSvc ? formatDateRange(openDate, closeDate) : "");

      const hsnCode = (item.HSNCODE || item.hsnCode || "").toString().trim();

      if (parsedData && (parsedData.slip || parsedData.driverAllowance > 0 || parsedData.tollTax > 0 || parsedData.parking > 0 || parsedData.nightCharges > 0 || parsedData.baseFare !== undefined || parsedData.pkg)) {
        // Extract route per day or single route
        const days = Array.isArray(parsedData.days) ? parsedData.days : [];

        // Clean & deduplicate package label
        const rawPkgString = (parsedData.pkg || rawService || "");
        const pkgParts = rawPkgString
          .split(/[,|]/)
          .map((p) => p.trim())
          .filter(Boolean);

        const uniquePkgParts = [];
        const seenPkgs = new Set();
        for (const p of pkgParts) {
          const norm = p.toUpperCase();
          if (!seenPkgs.has(norm)) {
            seenPkgs.add(norm);
            uniquePkgParts.push(p);
          }
        }
        let pkgLabel = uniquePkgParts.join(", ");

        if (!pkgLabel && days.length > 0) {
          const dPkgs = days
            .map((d) => (d.package || "").trim())
            .filter(Boolean);
          const uniqueDayPkgs = [];
          const seenDP = new Set();
          for (const dp of dPkgs) {
            const norm = dp.toUpperCase();
            if (!seenDP.has(norm)) {
              seenDP.add(norm);
              uniqueDayPkgs.push(dp);
            }
          }
          pkgLabel = uniqueDayPkgs.join(", ");
        }

        const sanitizeKmVal = (val, close, start, fallback = 0) => {
          const k = Number(val || 0);
          const c = Number(close || 0);
          const s = Number(start || 0);
          const f = Number(fallback || 0);
          if (k > 0 && k < 3000) return k;
          if (c > s && c > 0 && s > 0 && (c - s) < 3000) return c - s;
          if (f > 0 && f < 3000) return f;
          return 0;
        };

        const pkgType = (parsedData.packageType !== undefined && parsedData.packageType !== null)
          ? Number(parsedData.packageType)
          : (parsedData.PackageType !== undefined && parsedData.PackageType !== null)
            ? Number(parsedData.PackageType)
            : (days.some(d => d.packageType === 1) ? 1 : (/local|disposal/i.test(pkgLabel) ? 0 : 1));
        const isLocal = pkgType === 0;
        const isOutstation = !isLocal;

        const rawTotalKm = Number(parsedData.totalKM ?? parsedData.totalKm ?? parsedData.TotalKM ?? parsedData.km ?? 0);
        const sanitizedRawTotal = sanitizeKmVal(rawTotalKm, parsedData.kmClose, parsedData.kmStart, 0);

        const firstDayStart = Number(days[0]?.kmStart || days[0]?.kmStartGarage || days[0]?.kmStartUser || 0);
        const lastDayClose = Number(days[days.length - 1]?.kmClose || days[days.length - 1]?.kmCloseGarage || 0);
        const tripSpanKm = (lastDayClose > firstDayStart && firstDayStart > 0) ? (lastDayClose - firstDayStart) : 0;

        const daysTotalKm = days.reduce((sum, dy) => {
          const k = sanitizeKmVal(
            dy.km ?? dy.totalKm ?? dy.totalKM,
            dy.kmClose ?? dy.kmCloseGarage,
            dy.kmStart ?? dy.kmStartGarage ?? dy.kmStartUser,
            0
          );
          return sum + k;
        }, 0);
        const daysIncKm = days.reduce((sum, dy) => sum + Number(dy.incKM ?? dy.incKm ?? 0), 0);
        const daysBaseFare = days.reduce((sum, dy) => sum + Number(dy.baseFare ?? 0), 0);

        let totalKmVal = sanitizedRawTotal > 0 ? sanitizedRawTotal : (tripSpanKm > 0 ? tripSpanKm : 0);
        if (totalKmVal === 0 && daysTotalKm > 0) {
          totalKmVal = daysTotalKm;
        }

        const totalIncKmVal = daysIncKm > 0 ? daysIncKm : Number(parsedData.incKM || 0);
        const totalTimeVal = parsedData.totalTime ?? parsedData.TotalTime ?? parsedData.time ?? 0;
        const kmRateVal = Number(parsedData.kmRate || 0);

        const subRows = [];
        let baseFareAmt = daysBaseFare > 0
          ? daysBaseFare
          : (parsedData.baseFare !== undefined && parsedData.baseFare !== null
            ? Number(parsedData.baseFare)
            : ((Number(item.Rate || item.rate || item.Amount || item.amount) || 0) / (slipParts.length || 1)));

        let activeFuelRate = kmRateVal > 0 ? kmRateVal : 0;

        if (isOutstation) {
          const effKm = totalIncKmVal > 0 ? totalIncKmVal : 250;
          const effRate = activeFuelRate > 0 ? activeFuelRate : 0;
          activeFuelRate = effRate;
          baseFareAmt = effKm * effRate;
        }

        if (isLocal && days.length > 0) {
          days.forEach((dy, i) => {
            const dayPkg = (dy.package || "").trim();
            const prevPkg = i > 0 ? (days[i - 1]?.package || "").trim() : null;
            const firstPkg = (days[0]?.package || "").trim();

            // Show package name only on Day 1 (or if changed on subsequent days)
            let showPkg = false;
            let pkgToDisplay = "";
            if (i === 0) {
              pkgToDisplay = dayPkg || pkgLabel;
              showPkg = Boolean(pkgToDisplay);
            } else if (dayPkg && dayPkg.toUpperCase() !== (prevPkg || firstPkg).toUpperCase()) {
              showPkg = true;
              pkgToDisplay = dayPkg;
            }

            const dayDate = dy.date ? formatDate(dy.date) : dateDisplay;
            const dayKm = sanitizeKmVal(dy.km ?? dy.totalKm ?? dy.totalKM, dy.kmClose, dy.kmStart, 0);
            const dayTime = dy.time || 0;
            const rawDayBase = Number(dy.baseFare !== undefined && dy.baseFare !== null ? dy.baseFare : 0);
            const dayBaseFare = rawDayBase > 0 ? rawDayBase : (days.length === 1 ? baseFareAmt : (baseFareAmt / days.length));
            const dayRoute = (dy.route || "").trim();

            let dayParticulars = (showPkg && pkgToDisplay) ? `<div style="font-weight:bold; font-size:11px; color:#000;">${pkgToDisplay}</div>` : "";
            if (dayRoute) {
              dayParticulars += `<div style="font-size:10px; margin-top:2px;">Day ${i + 1} :- ${dayRoute}</div>`;
            }
            if (dayKm > 0 || dayTime) {
              const hasTime = dayTime && dayTime !== "0" && dayTime !== "0h" && dayTime !== "0h 0m" && dayTime !== "0.00" && dayTime !== 0;
              const timeText = hasTime ? `Total hr- ${dayTime} ` : "";
              const kmText = dayKm > 0 ? `Total Km- ${dayKm}` : "";
              if (timeText || kmText) {
                dayParticulars += `<div style="font-size:10px; margin-top:2px;">${timeText}${kmText}</div>`;
              }
            }

            subRows.push({
              hsn: (partIdx === 0 && i === 0) ? hsnCode : "",
              dateStr: dayDate,
              particulars: dayParticulars,
              rate: dayBaseFare > 0 ? dayBaseFare : "",
              amount: dayBaseFare > 0 ? dayBaseFare : "",
              opts: { compact: true }
            });
          });
        } else {
          const hasPerDayRoutes = days.some((d) => (d.route || "").trim().length > 0);
          let routeHtml = "";

          if (hasPerDayRoutes) {
            routeHtml = days
              .map((d, i) => {
                const r = (d.route || "").trim();
                return r ? `<div style="font-size:10px; margin-top:2px;">Day ${i + 1} :- ${r}</div>` : "";
              })
              .filter(Boolean)
              .join("");
          } else {
            const rawRouteStr = (parsedData.route || item.route || "").trim();
            const routeLegs = rawRouteStr
              .split(",")
              .map((l) => l.trim())
              .filter(Boolean)
              .filter((l, idx, self) => self.indexOf(l) === idx);
            if (routeLegs.length) {
              routeHtml = `<div style="font-size:10px; margin-top:2px;">Route :- ${routeLegs.join(", ")}</div>`;
            }
          }

          let baseParticulars = pkgLabel ? `<div style="font-weight:bold; font-size:11px; color:#000;">${pkgLabel}</div>` : "";
          baseParticulars += routeHtml;

          if (totalKmVal > 0) {
            const hasTime = !isOutstation && totalTimeVal && totalTimeVal !== "0" && totalTimeVal !== "0h" && totalTimeVal !== "0h 0m" && totalTimeVal !== "0.00" && totalTimeVal !== 0;
            const timeText = hasTime ? `Total hr- ${totalTimeVal} ` : "";
            const minKmText = totalIncKmVal > 0 ? ` (${totalIncKmVal} km min)` : "";
            baseParticulars += `<div style="font-size:10px; margin-top:2px;">${timeText}Total Km- ${totalKmVal}${minKmText}</div>`;
          } else if (totalTimeVal && !isOutstation) {
            const hasTime = totalTimeVal !== "0" && totalTimeVal !== "0h" && totalTimeVal !== "0h 0m" && totalTimeVal !== "0.00" && totalTimeVal !== 0;
            if (hasTime) {
              baseParticulars += `<div style="font-size:10px; margin-top:2px;">Total hr- ${totalTimeVal}</div>`;
            }
          }

          let rateDisplayStr = activeFuelRate > 0 ? activeFuelRate.toFixed(2) : (baseFareAmt > 0 ? baseFareAmt : "");

          subRows.push({
            hsn: partIdx === 0 ? hsnCode : "",
            dateStr: dateDisplay,
            particulars: baseParticulars,
            rate: rateDisplayStr,
            amount: baseFareAmt > 0 ? baseFareAmt : "",
            opts: { compact: true }
          });
        }

        const extraKmRate = activeFuelRate > 0 ? activeFuelRate : kmRateVal;
        const calcExtraKM = (totalIncKmVal > 0)
          ? Math.max(0, totalKmVal - totalIncKmVal)
          : Number(parsedData.extraKM || 0);
        const calcKmCharge = calcExtraKM * extraKmRate;

        if (parsedData.extraMins > 0 && Number(parsedData.timeCharge || 0) > 0) {
          const extraHrsVal = Number((parsedData.extraMins / 60).toFixed(2));
          const hrUnit = extraHrsVal === 1 ? "hr" : "hrs";
          subRows.push({
            hsn: "",
            dateStr: "",
            particulars: `<div style="font-size:10px; padding-left:8px;">Extra Time = ${extraHrsVal} ${hrUnit} x Rs.${Number(parsedData.timeRate || 0).toFixed(2)}/hr = Rs.${Number(parsedData.timeCharge).toFixed(2)}</div>`,
            rate: Number(parsedData.timeRate || 0),
            amount: Number(parsedData.timeCharge || 0),
            opts: { compact: true }
          });
        }

        if (calcExtraKM > 0 && calcKmCharge > 0) {
          subRows.push({
            hsn: "",
            dateStr: "",
            particulars: `<div style="font-size:10px; padding-left:8px;">Extra Km = ${calcExtraKM} km x Rs.${extraKmRate.toFixed(2)}/km = Rs.${calcKmCharge.toFixed(2)}</div>`,
            rate: extraKmRate,
            amount: calcKmCharge,
            opts: { compact: true }
          });
        }

        if (Number(parsedData.driverAllowance || 0) > 0) {
          subRows.push({
            hsn: "",
            dateStr: "",
            particulars: `<div style="font-size:10px; padding-left:8px;">Driver Allowance</div>`,
            rate: Number(parsedData.driverAllowance),
            amount: Number(parsedData.driverAllowance),
            opts: { compact: true }
          });
        }
        if (Number(parsedData.tollTax || 0) > 0) {
          subRows.push({
            hsn: "",
            dateStr: "",
            particulars: `<div style="font-size:10px; padding-left:8px;">Toll Tax</div>`,
            rate: Number(parsedData.tollTax),
            amount: Number(parsedData.tollTax),
            opts: { compact: true }
          });
        }
        if (Number(parsedData.parking || 0) > 0) {
          subRows.push({
            hsn: "",
            dateStr: "",
            particulars: `<div style="font-size:10px; padding-left:8px;">Parking</div>`,
            rate: Number(parsedData.parking),
            amount: Number(parsedData.parking),
            opts: { compact: true }
          });
        }
        if (Number(parsedData.nightCharges || 0) > 0) {
          subRows.push({
            hsn: "",
            dateStr: "",
            particulars: `<div style="font-size:10px; padding-left:8px;">Night Charges</div>`,
            rate: Number(parsedData.nightCharges),
            amount: Number(parsedData.nightCharges),
            opts: { compact: true }
          });
        }

        subRows.forEach((sr) => {
          itemRows += addRow(sr.hsn, sr.dateStr, sr.particulars, sr.rate, sr.amount, sr.opts);
          if (typeof sr.amount === "number") sumOfItemAmounts += sr.amount;
          totalRowCount++;
        });
      } else {
        const itemAmt = Number(item.Amount || item.amount || 0) / (slipParts.length || 1);
        const itemRate = Number(item.Rate || item.rate || itemAmt);
        const desc = singlePart || rawService || "";
        itemRows += addRow(hsnCode, dateDisplay, desc, itemRate > 0 ? itemRate : "", itemAmt > 0 ? itemAmt : "");
        sumOfItemAmounts += itemAmt;
        totalRowCount++;
      }
    });
  });

  const minRowsNeeded = 8;
  const blankRowsToAdd = Math.max(0, minRowsNeeded - totalRowCount);
  for (let i = 0; i < blankRowsToAdd; i++) {
    itemRows += `
<tr>
  <td style="border-left:1px solid #000; border-right:1px solid #000; border-bottom:none; height:24px;">&nbsp;</td>
  <td style="border-left:1px solid #000; border-right:1px solid #000; border-bottom:none; height:24px;">&nbsp;</td>
  <td style="border-left:1px solid #000; border-right:1px solid #000; border-bottom:none; height:24px;">&nbsp;</td>
  <td style="border-left:1px solid #000; border-right:1px solid #000; border-bottom:none; height:24px;">&nbsp;</td>
  <td style="border-left:1px solid #000; border-right:1px solid #000; border-bottom:none; height:24px;">&nbsp;</td>
</tr>`;
  }

  // Dynamically compute HSN summary groups and taxes from line items
  const hMap = {};
  let computedTaxableTotal = 0;

  allItems.forEach((item) => {
    const rawCode = (item.HSNCODE || item.hsnCode || "").toString().trim();
    const code = rawCode || "-";
    const itemTaxRate = (item.TaxRate !== undefined && item.TaxRate !== null && !isNaN(item.TaxRate))
      ? Number(item.TaxRate)
      : (fullInvoice.TaxRate !== undefined && fullInvoice.TaxRate !== null && !isNaN(fullInvoice.TaxRate) ? Number(fullInvoice.TaxRate) : 0);

    const pStr = item.Particulars || item.particulars || "";
    const pParts = (pStr.includes(" || ") && pStr.startsWith("{")) ? pStr.split(" || ") : [pStr];

    pParts.forEach((part) => {
      let itemAmt = 0;
      let pData = null;
      try {
        if (part.startsWith("{")) {
          pData = JSON.parse(part);
        }
      } catch (_) { }

      if (pData) {
        const days = Array.isArray(pData.days) ? pData.days : [];
        const daysBaseFare = days.reduce((sum, dy) => sum + Number(dy.baseFare ?? 0), 0);
        const parsedBaseFare = daysBaseFare > 0
          ? daysBaseFare
          : Number(pData.baseFare || 0);
        const isOutstation = pData.packageType === 1 || days.some(d => d.packageType === 1);
        let baseFareAmt = parsedBaseFare > 0
          ? parsedBaseFare
          : (Number(item.amount || item.Amount || 0) / (pParts.length || 1));
        if (isOutstation && baseFareAmt === 0) {
          const effKm = Number(pData.incKM || 0) > 0 ? Number(pData.incKM) : 250;
          const effRate = Number(pData.kmRate || 0);
          baseFareAmt = effKm * effRate;
        }
        const extraKmCharge = Number(pData.kmCharge || 0);
        const extraTimeCharge = Number(pData.timeCharge || 0);

        itemAmt = baseFareAmt + extraKmCharge + extraTimeCharge;

        const groupKey = `${code}_${itemTaxRate}`;
        if (!hMap[groupKey]) hMap[groupKey] = { HSNCODE: code, TaxRate: itemTaxRate, taxable: 0 };
        hMap[groupKey].taxable += itemAmt;

        const separateHsnCodes = new Set();
        allItems.forEach((other) => {
          const op = other.Particulars || other.particulars || "";
          if (!op.startsWith("{")) {
            const oc = (other.HSNCODE || other.hsnCode || "").toString().trim();
            if (oc && oc !== rawCode) separateHsnCodes.add(oc);
          }
        });
        const hasSeparateAddOns = separateHsnCodes.size > 0;

        if (!hasSeparateAddOns) {
          if (pData.parking > 0) hMap[groupKey].taxable += Number(pData.parking);
          if (pData.driverAllowance > 0) hMap[groupKey].taxable += Number(pData.driverAllowance);
          if (pData.tollTax > 0) hMap[groupKey].taxable += Number(pData.tollTax);
          if (pData.nightCharges > 0) hMap[groupKey].taxable += Number(pData.nightCharges);
        }
      } else {
        itemAmt = Number(item.Amount || item.amount || 0) / (pParts.length || 1);
        const groupKey = `${code}_${itemTaxRate}`;
        if (!hMap[groupKey]) hMap[groupKey] = { HSNCODE: code, TaxRate: itemTaxRate, taxable: 0 };
        hMap[groupKey].taxable += itemAmt;
      }
    });
  });

  const activeHsnList = Object.values(hMap);
  let hsnCgstTotal = 0, hsnSgstTotal = 0, hsnIgstTotal = 0, hsnOverallTax = 0;

  activeHsnList.forEach((h) => {
    computedTaxableTotal += h.taxable;
    const rate = Number(h.TaxRate || 0);
    const taxAmt = Math.round((h.taxable * rate / 100) * 100) / 100;
    const cgstAmt = isInterState ? 0 : Math.round((taxAmt / 2) * 100) / 100;
    const sgstAmt = isInterState ? 0 : Math.round((taxAmt - cgstAmt) * 100) / 100;
    const igstAmt = isInterState ? taxAmt : 0;

    hsnCgstTotal += cgstAmt;
    hsnSgstTotal += sgstAmt;
    hsnIgstTotal += igstAmt;
    hsnOverallTax += taxAmt;
  });

  const activeTaxableAmount = sumOfItemAmounts > 0 ? sumOfItemAmounts : (computedTaxableTotal > 0 ? computedTaxableTotal : totalAmount);
  const activeTaxTotal = fullInvoice.totalTax !== undefined && fullInvoice.totalTax !== null
    ? Number(fullInvoice.totalTax)
    : hsnOverallTax;
  const activeNetPayable = (activeTaxableAmount + (isReverseCharge ? 0 : activeTaxTotal)) - lessAdvance;

  return `
<div class="tax-invoice-document" style="font-family: Arial, sans-serif; color: #000; padding: 20px 24px; width: 794px; min-height: 1050px; box-sizing: border-box; background: #fff; margin: 0 auto; display: block;">
  <style>
    * { box-sizing: border-box; }
    .tax-inv-table th, .tax-inv-table td { border: 1px solid #000; }
  </style>

  <!-- Header -->
  <table style="width:100%; border-collapse:collapse; margin-bottom:18px; border:none;">
    <tr>
      <td style="vertical-align:top; border:none; padding:0;">
        <div style="display:flex; align-items:flex-start; gap:16px;">
          ${logoSrc ? `
            <div class="company-logo-wrap" style="height:60px; max-width:140px; display:inline-block; vertical-align:top; overflow:hidden;">
              <img
                class="company-logo"
                src="${logoSrc}"
                alt="logo"
                style="height:60px; max-height:60px; max-width:140px; width:auto; object-fit:contain;"
              />
            </div>
          ` : ""}
          <div style="display:inline-block; vertical-align:top; margin-left:${logoSrc ? '16px' : '0'};">
            <div style="font-size:18px; font-weight:800; letter-spacing:0.5px; text-transform:uppercase; color:${companyColor || '#000000'}; line-height:1.2; margin-bottom:4px;">${companyName || "COMPANY NAME"}</div>
            ${companyAddress ? `<div style="font-size:11.5px; color:#374151; line-height:1.4;">${companyAddress}</div>` : ""}
            ${companyGSTIN ? `<div style="font-size:11.5px; color:#111827; line-height:1.4; margin-top:2px;"><span style="font-weight:700;">GSTIN:</span> <span style="font-weight:700; letter-spacing:0.3px;">${companyGSTIN}</span></div>` : ""}
          </div>
        </div>
      </td>
      <td style="text-align:right; vertical-align:top; width:220px; border:none; padding:0;">
        <div style="font-family: Arial, sans-serif; font-size:16px; font-weight:800; line-height:1.2; letter-spacing:0.5px; text-decoration:underline; text-underline-offset:3px; color:#000000; padding-top:1px;">TAX INVOICE</div>
      </td>
    </tr>
  </table>

  <!-- Details Section -->
  <table style="width:100%; border-collapse:collapse; margin-bottom:16px; border:none;">
    <tr>
      <td style="width:49%; vertical-align:top; border:1px solid #000; padding:8px 10px;">
        <table style="width:100%; border-collapse:collapse; font-size:12px; line-height:1.6; border:none;">
          <tr><td style="white-space:nowrap; vertical-align:top; padding:2px 8px 2px 0; border:none;">Invoice No.</td><td style="text-align:right; font-weight:bold; vertical-align:top; padding:2px 0; border:none;">${formatInvoiceNumber(invoiceNo, invoiceDate, companyData?.CompanyCode || "DDT")}</td></tr>
          <tr><td style="white-space:nowrap; vertical-align:top; padding:2px 8px 2px 0; border:none;">Invoice Date</td><td style="text-align:right; font-weight:bold; vertical-align:top; padding:2px 0; border:none;">${formatDateToDMY(invoiceDate)}</td></tr>
          <tr><td style="white-space:nowrap; vertical-align:top; padding:2px 8px 2px 0; border:none;">Party / Account</td><td style="text-align:right; font-weight:bold; vertical-align:top; padding:2px 0; border:none;">${partyName}</td></tr>
          ${gstin ? `<tr><td style="white-space:nowrap; vertical-align:top; padding:2px 8px 2px 0; border:none;">GSTIN</td><td style="text-align:right; vertical-align:top; padding:2px 0; border:none;">${gstin}</td></tr>` : ""}
          ${address ? `<tr><td style="white-space:nowrap; vertical-align:top; padding:2px 8px 2px 0; border:none;">Address</td><td style="text-align:right; vertical-align:top; padding:2px 0; border:none;">${address}</td></tr>` : ""}
          <tr><td style="white-space:nowrap; vertical-align:top; padding:2px 8px 2px 0; border:none;">State / Code</td><td style="text-align:right; vertical-align:top; padding:2px 0; border:none;">${state || ""}${stateCode ? ` (${stateCode})` : ""}</td></tr>
          <tr><td style="white-space:nowrap; vertical-align:top; padding:2px 8px 2px 0; border:none;">Reverse Charge</td><td style="text-align:right; vertical-align:top; padding:2px 0; border:none;">${reverseCharge || "No"}</td></tr>
        </table>
      </td>
      <td style="width:2%; border:none;"></td>
      <td style="width:49%; vertical-align:top; border:1px solid #000; padding:8px 10px;">
        <table style="width:100%; border-collapse:collapse; font-size:12px; line-height:1.6; border:none;">
          ${bankData?.BankName ? `<tr><td style="white-space:nowrap; vertical-align:top; padding:2px 8px 2px 0; border:none;">Bank Name</td><td style="text-align:right; font-weight:bold; vertical-align:top; padding:2px 0; border:none;">${bankData.BankName}</td></tr>` : ""}
          ${bankData?.BankAccountNo ? `<tr><td style="white-space:nowrap; vertical-align:top; padding:2px 8px 2px 0; border:none;">Bank Account No</td><td style="text-align:right; font-weight:bold; vertical-align:top; padding:2px 0; border:none;">${bankData.BankAccountNo}</td></tr>` : ""}
          ${(bankData?.IFSCCode || bankData?.PanNo) ? `<tr><td style="white-space:nowrap; vertical-align:top; padding:2px 8px 2px 0; border:none;">IFSC / PAN</td><td style="text-align:right; vertical-align:top; padding:2px 0; border:none;">${bankData.IFSCCode || ""}${bankData.PanNo ? ` / ${bankData.PanNo}` : ""}</td></tr>` : ""}
          ${dutySlipNoDisplay ? `<tr><td style="white-space:nowrap; vertical-align:top; padding:2px 8px 2px 0; border:none;">Duty Slip No #</td><td style="text-align:right; font-weight:bold; vertical-align:top; padding:2px 0; border:none;">${dutySlipNoDisplay}</td></tr>` : ""}
          ${vehicleDisplay ? `<tr><td style="white-space:nowrap; vertical-align:top; padding:2px 8px 2px 0; border:none;">Vehicle No / Type</td><td style="text-align:right; vertical-align:top; padding:2px 0; border:none;">${vehicleDisplay}</td></tr>` : ""}
          ${driverName ? `<tr><td style="white-space:nowrap; vertical-align:top; padding:2px 8px 2px 0; border:none;">Driver Name</td><td style="text-align:right; vertical-align:top; padding:2px 0; border:none;">${driverName}</td></tr>` : ""}
          ${(guest || bookedBy) ? `<tr><td style="white-space:nowrap; vertical-align:top; padding:2px 8px 2px 0; border:none;">Guest</td><td style="text-align:right; vertical-align:top; padding:2px 0; border:none;">${guest}${bookedBy ? ` (${bookedBy})` : ""}</td></tr>` : ""}
        </table>
      </td>
    </tr>
  </table>

  <!-- Table -->
  <table style="width:100%; border-collapse:collapse; font-size:13px; border:1px solid #000;">
    <thead>
      <tr style="background:#f2f2f2;">
        <th style="border:1px solid #000; text-align:center; padding:9px 5px; width:75px;">HSN Code</th>
        <th style="border:1px solid #000; text-align:center; padding:9px 5px; width:105px;">Date</th>
        <th style="border:1px solid #000; text-align:center; padding:9px 5px; width:380px;">Particulars</th>
        <th style="border:1px solid #000; text-align:center; padding:9px 5px; width:90px;">Rate (Rs.)</th>
        <th style="border:1px solid #000; text-align:center; padding:9px 5px; width:100px;">Amount (Rs.)</th>
      </tr>
    </thead>
    <tbody>
      ${itemRows}
    </tbody>
  </table>

  <!-- Totals Section -->
  <table style="width:100%; border-collapse:collapse; margin-top:5px; font-size:13px; border:none;">
    <tr>
      <td style="vertical-align:top; padding-right:18px; padding-top:6px; border:none;">
        <strong>In Words:</strong> ${numberToWords(activeNetPayable)}
      </td>
      <td style="vertical-align:top; width:330px; padding:0; border:none;">
        <table style="width:100%; border-collapse:collapse; font-size:12px; border:1px solid #000;">
          <tr>
            <td style="padding:6px 12px; vertical-align:middle; border:none; border-bottom:1px solid #eee;">Total Taxable Amount</td>
            <td style="padding:6px 12px; text-align:right; font-weight:bold; vertical-align:middle; border:none; border-bottom:1px solid #eee;">Rs.${formatNumber(activeTaxableAmount)}</td>
          </tr>
          <tr>
            <td style="padding:4px 12px; font-size:11px; color:#333; vertical-align:middle; border:none; border-bottom:1px solid #eee;">Total GST</td>
            <td style="padding:4px 12px; text-align:right; font-size:11px; font-weight:bold; vertical-align:middle; border:none; border-bottom:1px solid #eee;">Rs.${formatNumber(activeTaxTotal)}</td>
          </tr>
          ${isReverseCharge ? `
          <tr>
            <td style="padding:4px 12px; font-size:11px; color:#333; vertical-align:middle; border:none; border-bottom:1px solid #eee;">GST Payable on Reverse Charge</td>
            <td style="padding:4px 12px; text-align:right; font-size:11px; font-weight:bold; vertical-align:middle; border:none; border-bottom:1px solid #eee;">Rs.${formatNumber(activeTaxTotal)}</td>
          </tr>` : ''}
          ${lessAdvance > 0 ? `
          <tr>
            <td style="padding:4px 12px; font-size:11px; color:#333; vertical-align:middle; border:none; border-bottom:1px solid #eee;">Less Advance</td>
            <td style="padding:4px 12px; text-align:right; font-size:11px; font-weight:bold; color:#dc2626; vertical-align:middle; border:none; border-bottom:1px solid #eee;">- Rs.${formatNumber(lessAdvance)}</td>
          </tr>` : ''}
          <tr style="border-top:1px solid #000; background:#f2f2f2;">
            <td style="padding:6px 12px; vertical-align:middle; font-weight:bold; font-size:11px; color:#000; border:none;">Grand Total</td>
            <td style="padding:6px 12px; text-align:right; vertical-align:middle; font-weight:bold; font-size:11px; color:#000; border:none;">Rs.${formatNumber(activeNetPayable)}</td>
          </tr>
        </table>
      </td>
    </tr>
  </table>

  <!-- HSN Summary -->
  <div style="margin-top:16px;">
    <div style="font-weight:bold; font-size:11px; margin-bottom:5px; text-transform:uppercase; letter-spacing:0.5px; color:#000;">HSN SUMMARY</div>
    <table style="border-collapse:collapse; font-size:10px; border:1px solid #000; text-align:center;">
      <thead>
        <tr style="background:#f2f2f2; color:#000; font-weight:bold;">
          <th style="border:1px solid #000; padding:6px 8px; text-align:center; vertical-align:middle; white-space:nowrap;">HSN Code</th>
          <th style="border:1px solid #000; padding:6px 8px; text-align:center; vertical-align:middle; white-space:nowrap;">CGST Rate</th>
          <th style="border:1px solid #000; padding:6px 8px; text-align:center; vertical-align:middle; white-space:nowrap;">CGST Amount</th>
          <th style="border:1px solid #000; padding:6px 8px; text-align:center; vertical-align:middle; white-space:nowrap;">SGST Rate</th>
          <th style="border:1px solid #000; padding:6px 8px; text-align:center; vertical-align:middle; white-space:nowrap;">SGST Amount</th>
          <th style="border:1px solid #000; padding:6px 8px; text-align:center; vertical-align:middle; white-space:nowrap;">IGST Rate</th>
          <th style="border:1px solid #000; padding:6px 8px; text-align:center; vertical-align:middle; white-space:nowrap;">IGST Amount</th>
          <th style="border:1px solid #000; padding:6px 8px; text-align:center; vertical-align:middle; white-space:nowrap;">Total Tax</th>
        </tr>
      </thead>
      <tbody>
        ${(() => {
          const cell = (content, bold = false) =>
            `<td style="border:1px solid #000; padding:5px 8px; text-align:center; vertical-align:middle; white-space:nowrap; ${bold ? "font-weight:bold;" : ""}">${content}</td>`;

          const rowsHtml = activeHsnList.map((h) => {
            const rate = Number(h.TaxRate || 0);
            const taxAmt = Math.round((h.taxable * rate / 100) * 100) / 100;
            const cgstRate = isInterState ? 0 : rate / 2;
            const cgstAmt = isInterState ? 0 : Math.round((taxAmt / 2) * 100) / 100;
            const sgstRate = isInterState ? 0 : rate / 2;
            const sgstAmt = isInterState ? 0 : Math.round((taxAmt - cgstAmt) * 100) / 100;
            const igstRate = isInterState ? rate : 0;
            const igstAmt = isInterState ? taxAmt : 0;

            return `
          <tr>
            ${cell(h.HSNCODE)}
            ${cell(cgstRate > 0 ? cgstRate.toFixed(2) + "%" : "–")}
            ${cell(cgstAmt > 0 ? "Rs." + cgstAmt.toFixed(2) : "–")}
            ${cell(sgstRate > 0 ? sgstRate.toFixed(2) + "%" : "–")}
            ${cell(sgstAmt > 0 ? "Rs." + sgstAmt.toFixed(2) : "–")}
            ${cell(igstRate > 0 ? igstRate.toFixed(2) + "%" : "–")}
            ${cell(igstAmt > 0 ? "Rs." + igstAmt.toFixed(2) : "–")}
            ${cell("Rs." + taxAmt.toFixed(2), true)}
          </tr>`;
          }).join("");

          const footerHtml = `
          <tr style="background:#f9f9f9;">
            ${cell("Total", true)}
            ${cell("–")}
            ${cell(hsnCgstTotal > 0 ? "Rs." + hsnCgstTotal.toFixed(2) : "–", true)}
            ${cell("–")}
            ${cell(hsnSgstTotal > 0 ? "Rs." + hsnSgstTotal.toFixed(2) : "–", true)}
            ${cell("–")}
            ${cell(hsnIgstTotal > 0 ? "Rs." + hsnIgstTotal.toFixed(2) : "–", true)}
            ${cell("Rs." + hsnOverallTax.toFixed(2), true)}
          </tr>`;

          return rowsHtml + footerHtml;
        })()}
      </tbody>
    </table>
  </div>

  <!-- Equal Row: Declaration & Signature (Unified Matching Font) -->
  <table style="width:100%; border-collapse:collapse; margin-top:20px; border:none; page-break-inside:avoid;">
    <tr>
      <!-- Left: Declaration -->
      <td style="width:55%; vertical-align:top; padding:0; border:none;">
        <div style="font-family: Arial, sans-serif; font-weight:bold; font-size:11px; margin-bottom:4px; text-transform:uppercase; letter-spacing:0.5px; color:#000;">DECLARATION</div>
        <div style="font-family: Arial, sans-serif; border:1px solid #000; padding:6px 8px; line-height:1.45; font-size:9.5px; color:#111;">
          We declare that this invoice shows the actual price of the goods described
          and that all particulars are true and correct.
        </div>
        <div style="font-family: Arial, sans-serif; margin-top:5px; font-size:9px; color:#555;">
          All disputes subject to Jaipur jurisdiction only.
        </div>
      </td>

      <td style="width:5%; border:none;"></td>

      <!-- Right: For Company / Authorized Signature -->
      <td style="width:40%; vertical-align:bottom; text-align:right; padding:0; border:none; white-space:nowrap;">
        <div style="display:inline-block; text-align:center; min-width:190px;">
          <div style="font-family: Arial, sans-serif; font-weight:bold; font-size:11px; color:#000; margin-bottom:45px; letter-spacing:0.5px;">For ${companyName}</div>
          <div style="font-family: Arial, sans-serif; font-weight:bold; font-size:11px; color:#000; letter-spacing:0.5px;">Authorized Signature</div>
        </div>
      </td>
    </tr>
  </table>
</div>
`;
};

export const downloadInvoice = async (invoiceOrInvNo, onProgress) => {
  try {
    const selectedCompany = JSON.parse(localStorage.getItem("selectedCompany")) || {};
    const CompID = localStorage.getItem("companyId") || selectedCompany?.id || selectedCompany?.CompID;

    const invoiceObj = typeof invoiceOrInvNo === "object" && invoiceOrInvNo !== null
      ? invoiceOrInvNo
      : { InvoiceNo: invoiceOrInvNo, invId: invoiceOrInvNo };
    const invoiceNo = invoiceObj.InvoiceNo || invoiceObj.invId;

    if (!CompID) {
      return showToast.error("Company not selected!");
    }

    downloadProgress.start(`Tax Invoice PDF`, "Connecting to database...");
    if (onProgress) onProgress(15, "Connecting to database...");
    downloadProgress.update(15, "Connecting to database...");
    let companyData = {};
    try {
      const companyRes = await axios.get(
        `${BASE_API_URL}/invoice/company/${CompID}`,
        { timeout: 5000 },
      );
      if (companyRes.data?.success) {
        companyData = companyRes.data.data || {};
      }
    } catch (err) {
      console.warn("Failed to fetch company info for invoice PDF", err);
    }
    const restCompanyData = await fetchRestCompany(CompID).catch((err) => {
      console.warn("Failed to fetch company logo for invoice PDF", err);
      return {};
    });
    const resolvedCompanyData = {
      ...restCompanyData,
      ...companyData,
      LogoPath:
        companyData.LogoPath ||
        companyData.Logopath ||
        companyData.logoPath ||
        restCompanyData.LogoPath,
    };
    const companyLogoUrl = buildLogoUrl(
      resolvedCompanyData.LogoPath || resolvedCompanyData.Logopath || resolvedCompanyData.logoPath ||
      selectedCompany?.LogoPath || selectedCompany?.Logopath || selectedCompany?.logoPath
    );

    const getPdfLogoUrl = (imageUrl) => {
      if (!imageUrl) return null;
      try {
        const parsed = new URL(imageUrl);
        const proxyApiUrl = BASE_API_URL;
        return `${proxyApiUrl}/invoice/logo-proxy?path=${encodeURIComponent(
          parsed.pathname,
        )}`;
      } catch (err) {
        return imageUrl;
      }
    };

    const pdfLogoUrl = getPdfLogoUrl(companyLogoUrl);

    const loadImageAsDataUrl = async (imageUrl) => {
      if (!imageUrl) return null;
      try {
        const response = await axios.get(imageUrl, {
          responseType: "blob",
          timeout: 5000,
        });
        const blob = response.data;
        return await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        console.warn("Unable to load logo image as blob for PDF:", imageUrl, error);
        return null;
      }
    };

    const ensureRemoteImageLoads = async (imageUrl) => {
      if (!imageUrl) return null;
      return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(imageUrl);
        img.onerror = () => resolve(null);
        img.src = imageUrl;
        setTimeout(() => resolve(null), 10000);
      });
    };

    let inlineCompanyLogo = await loadImageAsDataUrl(pdfLogoUrl);
    if (!inlineCompanyLogo && pdfLogoUrl) {
      inlineCompanyLogo = await ensureRemoteImageLoads(pdfLogoUrl);
    }
    const logoSrc = inlineCompanyLogo || pdfLogoUrl || companyLogoUrl;

    const waitForImageLoad = async (parent) => {
      const img = parent.querySelector("img.company-logo");
      if (!img) return;
      if (img.complete && img.naturalWidth !== 0) return;
      await new Promise((resolve) => {
        const timeout = setTimeout(resolve, 10000);
        img.addEventListener("load", () => {
          clearTimeout(timeout);
          resolve();
        });
        img.addEventListener("error", () => {
          clearTimeout(timeout);
          resolve();
        });
      });
    };

    if (onProgress) onProgress(45, "Loading invoice details & bank info...");
    downloadProgress.update(45, "Loading invoice details & bank info...");
    const res = await axios.get(
      `${BASE_API_URL}/invoice/details/${CompID}?invoiceNo=${encodeURIComponent(invoiceNo)}`
    );
    let fullInvoice = res.data?.data || {};

    if (typeof invoiceOrInvNo === "object" && invoiceOrInvNo !== null) {
      fullInvoice = {
        ...invoiceObj,
        ...fullInvoice,
      };
      [
        "State", "state", "StateName", "stateName", "StateCode", "stateCode",
        "Address", "address", "PartyAddress", "partyAddress",
        "GSTIN", "gstin", "PartyGSTIN", "partyGSTIN", "GstNo",
        "VehicleNo", "vehicleNo", "VehicleType", "vehicleType",
        "DriverName", "driverName", "Guest", "guest", "BookedBy", "bookedBy",
        "ReverseCharge", "reverseCharge", "ReverseCharges",
      ].forEach((f) => {
        if ((fullInvoice[f] === undefined || fullInvoice[f] === null || fullInvoice[f] === "") && invoiceObj[f]) {
          fullInvoice[f] = invoiceObj[f];
        }
      });
      if (invoiceObj.State || invoiceObj.state || invoiceObj.StateName) {
        fullInvoice.State = invoiceObj.State || invoiceObj.state || invoiceObj.StateName;
      }
      if (invoiceObj.StateCode || invoiceObj.stateCode) {
        fullInvoice.StateCode = invoiceObj.StateCode || invoiceObj.stateCode;
      }
      if (invoiceObj.Address || invoiceObj.address || invoiceObj.PartyAddress) {
        fullInvoice.Address = invoiceObj.Address || invoiceObj.address || invoiceObj.PartyAddress;
      }
      if (invoiceObj.GSTIN || invoiceObj.gstin || invoiceObj.PartyGSTIN) {
        fullInvoice.GSTIN = invoiceObj.GSTIN || invoiceObj.gstin || invoiceObj.PartyGSTIN;
      }
    }

    const pName = fullInvoice.PartyName || fullInvoice.company || fullInvoice.partyName;
    if (pName && (!fullInvoice.State || !fullInvoice.StateCode || !fullInvoice.Address || !fullInvoice.GSTIN)) {
      try {
        const partyRes = await axios.get(
          `${BASE_API_URL}/invoice/dropdowns/details/${CompID}?headName=${encodeURIComponent(pName)}`
        );
        if (partyRes.data?.success && partyRes.data?.data) {
          const p = partyRes.data.data;
          if (!fullInvoice.State && !fullInvoice.state) fullInvoice.State = p.StateName || p.State || "";
          if (!fullInvoice.StateCode && !fullInvoice.stateCode) fullInvoice.StateCode = p.StateCode || "";
          if (!fullInvoice.Address && !fullInvoice.address && !fullInvoice.PartyAddress) fullInvoice.Address = p.ADDRESS1 || p.Address || "";
          if (!fullInvoice.GSTIN && !fullInvoice.gstin && !fullInvoice.PartyGSTIN && !fullInvoice.GstNo) fullInvoice.GSTIN = p.GSTNO || p.GstNo || "";
        }
      } catch (err) {
        console.warn("Could not fetch party fallback details for invoice PDF", err);
      }
    }

    let bankData = null;
    try {
      const bankRes = await axios.get(
        `${BASE_API_URL}/company/bank-details/${CompID}`
      );
      if (bankRes.data.success) {
        bankData = bankRes.data.data;
      }
    } catch (err) {
      console.error("BANK ERROR:", err);
    }

    if (onProgress) onProgress(75, "Rendering pixel-perfect document canvas...");
    downloadProgress.update(75, "Rendering pixel-perfect document canvas...");
    const element = document.createElement("div");
    element.style.position = "fixed";
    element.style.top = "-9999px";
    element.style.left = "-9999px";
    element.style.width = "794px";
    element.style.backgroundColor = "#ffffff";
    element.innerHTML = buildInvoiceHtml({
      fullInvoice,
      companyData: resolvedCompanyData,
      bankData,
      logoSrc,
    });

    document.body.appendChild(element);
    await waitForImageLoad(element);

    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      allowTaint: false,
    });
    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    // A4 margins (mm) for centered professional look
    const marginX = 5;
    const marginY = 8;
    const availableWidth = pdfWidth - 2 * marginX;
    const availableHeight = pdfHeight - 2 * marginY;

    const imgProps = pdf.getImageProperties(imgData);
    let imgWidth = availableWidth;
    let imgHeight = (imgProps.height * availableWidth) / imgProps.width;

    // If the scaled image is taller than the available height, scale down to fit
    if (imgHeight > availableHeight) {
      const scaleFactor = availableHeight / imgHeight;
      imgHeight = availableHeight;
      imgWidth = imgWidth * scaleFactor;
    }

    // Center horizontally and vertically on the page
    const xOffset = (pdfWidth - imgWidth) / 2;
    const yOffset = (pdfHeight - imgHeight) / 2;
    pdf.addImage(imgData, "PNG", xOffset, yOffset, imgWidth, imgHeight);
    if (onProgress) onProgress(95, "Compiling A4 PDF...");
    downloadProgress.update(95, "Compiling A4 PDF...");
    pdf.save(`Invoice_${invoiceNo}.pdf`);
    document.body.removeChild(element);
    if (onProgress) {
      onProgress(100, "Invoice downloaded successfully!");
    }
    downloadProgress.complete(`Invoice #${invoiceNo} downloaded successfully!`, "Downloaded Tax Invoice PDF");
  } catch (err) {
    console.error("PDF ERROR:", err);
    downloadProgress.error("Failed to generate invoice PDF!");
    showToast.error("Failed to generate invoice PDF!");
  }
};
